/**
 * Analytics tracking utilities for CodiceCivile.AI
 * Provides event tracking for user interactions and conversions
 */

interface QueuedEvent {
  event: string;
  properties: Record<string, any>;
}

class Analytics {
  private isEnabled: boolean;
  private eventQueue: QueuedEvent[] = [];
  private gtagReady: boolean = false;
  private flushCheckInterval: number | null = null;

  constructor() {
    // Enable analytics in production only
    this.isEnabled = import.meta.env.PROD;

    // Start checking for gtag availability
    this.startGtagCheck();
  }

  /**
   * Check for gtag availability and flush queue when ready
   */
  private startGtagCheck(): void {
    // Check immediately
    this.checkGtagReady();

    // Set up periodic check
    this.flushCheckInterval = window.setInterval(() => {
      this.checkGtagReady();
    }, 100);
  }

  /**
   * Check if gtag is ready and flush queue if so
   */
  private checkGtagReady(): void {
    if (!this.gtagReady && window.gtag && typeof window.gtag === 'function') {
      this.gtagReady = true;

      // Clear the check interval
      if (this.flushCheckInterval) {
        clearInterval(this.flushCheckInterval);
        this.flushCheckInterval = null;
      }

      // Flush queued events
      this.flushQueue();
    }
  }

  /**
   * Flush all queued events to gtag
   */
  private flushQueue(): void {
    if (!this.gtagReady || !window.gtag) {
      return;
    }

    while (this.eventQueue.length > 0) {
      const queuedEvent = this.eventQueue.shift();
      if (queuedEvent) {
        try {
          window.gtag('event', queuedEvent.event, queuedEvent.properties);
        } catch (error) {
          console.error('Analytics queue flush error:', error);
        }
      }
    }
  }

  /**
   * Track a custom event
   */
  track(event: string, properties: Record<string, any> = {}): void {
    if (!this.isEnabled) {
      console.log('[Analytics - Dev Mode]', event, properties);
      return;
    }

    // If gtag is ready, send immediately
    if (this.gtagReady && window.gtag) {
      try {
        window.gtag('event', event, properties);
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
    } else {
      // Queue the event for later
      this.eventQueue.push({ event, properties });

      // Limit queue size to prevent memory issues
      if (this.eventQueue.length > 50) {
        this.eventQueue.shift(); // Remove oldest event
      }
    }
  }

  /**
   * Track page view
   */
  pageView(path: string, title?: string): void {
    this.track('page_view', {
      path,
      title: title || document.title,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track user identification
   */
  identify(userId: string, traits: Record<string, any> = {}): void {
    if (!this.isEnabled) {
      console.log('[Analytics - Dev Mode] Identify:', userId, traits);
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('set', 'user_properties', {
          user_id: userId,
          ...traits,
        });
      }
    } catch (error) {
      console.error('Analytics identify error:', error);
    }
  }
}

// Create singleton instance
const analytics = new Analytics();

/**
 * Track guarantee badge clicks
 * @param location - Where the badge was clicked (e.g., 'pricing_page', 'subscription_card', 'checkout')
 */
export const trackGuaranteeClick = (location: string): void => {
  analytics.track('guarantee_badge_clicked', {
    location,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track subscription plan selection
 */
export const trackPlanSelection = (planName: string, planPrice: number, location: string): void => {
  analytics.track('plan_selected', {
    plan_name: planName,
    plan_price: planPrice,
    location,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track checkout initiated
 */
export const trackCheckoutInitiated = (planName: string, planPrice: number): void => {
  analytics.track('checkout_initiated', {
    plan_name: planName,
    plan_price: planPrice,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track subscription completed
 */
export const trackSubscriptionCompleted = (planName: string, planPrice: number): void => {
  analytics.track('subscription_completed', {
    plan_name: planName,
    plan_price: planPrice,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track usage limit reached
 */
export const trackUsageLimitReached = (usage: number, limit: number): void => {
  analytics.track('usage_limit_reached', {
    usage,
    limit,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track upgrade prompt shown
 */
export const trackUpgradePromptShown = (location: string, reason: string): void => {
  analytics.track('upgrade_prompt_shown', {
    location,
    reason,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track upgrade prompt clicked
 */
export const trackUpgradePromptClicked = (location: string): void => {
  analytics.track('upgrade_prompt_clicked', {
    location,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track onboarding tour events
 */
export const trackTourEvent = (event: string, step?: number, metadata?: Record<string, any>): void => {
  analytics.track(event, {
    step,
    timestamp: new Date().toISOString(),
    ...metadata,
  });
};

/**
 * Track tour started
 */
export const trackTourStarted = (): void => {
  trackTourEvent('tour_started');
};

/**
 * Track tour step completed
 */
export const trackTourStepCompleted = (step: number, stepName: string): void => {
  trackTourEvent('tour_step_completed', step, { step_name: stepName });
};

/**
 * Track tour skipped
 */
export const trackTourSkipped = (step: number, stepName: string): void => {
  trackTourEvent('tour_skipped', step, {
    step_name: stepName,
    reason: 'user_skipped'
  });
};

/**
 * Track tour completed
 */
export const trackTourCompleted = (): void => {
  trackTourEvent('tour_completed');
};

/**
 * Track tour restarted
 */
export const trackTourRestarted = (location: string): void => {
  trackTourEvent('tour_restarted', undefined, { location });
};

// Export the analytics instance for direct use
export default analytics;

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
